import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2, Inbox, AlertTriangle, Clock, CalendarDays, Calendar,
  ArrowRight, ListChecks, Search, Circle, Loader2, RefreshCw, Download, Repeat,
  ChevronLeft, ChevronRight, LayoutList, Bookmark, BookmarkPlus, ChevronDown,
} from "lucide-react";

const downloadCsv = async (url, filename) => {
  try {
    const token = localStorage.getItem("token");
    const BASE = process.env.REACT_APP_API_URL || "";
    const res = await fetch(`${BASE}/api${url}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("Export failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    // silent — user sees no toast on download pages
  }
};
import { toast } from "sonner";

// ── Calendar helpers ──────────────────────────────────────────────────────────
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function calendarDays(year, month) {
  const first = new Date(year, month, 1).getDay();
  const last = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= last; d++) cells.push(d);
  return cells;
}

const PRIORITY_DOT = { critical: "bg-[#FF007A]", high: "bg-[#F59E0B]", medium: "bg-[#00F5D4]", low: "bg-[#374151]" };

const TaskCalendar = ({ tasks, updateStatus }) => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [selected, setSelected] = useState(null);

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); setSelected(null); };
  const next = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); setSelected(null); };

  // Map tasks to day numbers within current month
  const tasksByDay = useMemo(() => {
    const map = {};
    for (const t of tasks) {
      if (!t.due_date || t.status === "done") continue;
      const d = new Date(t.due_date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(t);
      }
    }
    return map;
  }, [tasks, year, month]);

  const cells = calendarDays(year, month);
  const todayDay = today.getFullYear() === year && today.getMonth() === month ? today.getDate() : null;
  const selectedTasks = selected ? (tasksByDay[selected] || []) : [];

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-6">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-5">
        <button onClick={prev} className="p-2 rounded-lg hover:bg-[#1F2937] text-slate-400 hover:text-white transition" data-testid="cal-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h2 className="font-bold text-lg text-white tracking-tight">
          {MONTHS[month]} <span className="text-slate-500">{year}</span>
        </h2>
        <button onClick={next} className="p-2 rounded-lg hover:bg-[#1F2937] text-slate-400 hover:text-white transition" data-testid="cal-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => (
          <div key={d} className="text-center text-[10px] uppercase tracking-[0.2em] text-slate-600 font-mono py-2">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-px bg-[#1F2937] border border-[#1F2937] rounded-lg overflow-hidden">
        {cells.map((day, i) => {
          const dayTasks = day ? (tasksByDay[day] || []) : [];
          const isToday = day === todayDay;
          const isSelected = day === selected;
          const hasOverdue = dayTasks.some(t => {
            const d = new Date(t.due_date); d.setHours(0,0,0,0);
            const now = new Date(); now.setHours(0,0,0,0);
            return d < now;
          });
          return (
            <div
              key={i}
              onClick={() => day && dayTasks.length > 0 && setSelected(isSelected ? null : day)}
              className={`bg-[#0D1117] min-h-[72px] p-2 transition-colors ${
                day && dayTasks.length > 0 ? "cursor-pointer hover:bg-[#111827]" : ""
              } ${isSelected ? "bg-[#111827] ring-1 ring-inset ring-[#002FA7]" : ""}`}
              data-testid={day ? `cal-day-${day}` : `cal-empty-${i}`}
            >
              {day && (
                <>
                  <div className={`text-xs font-mono w-6 h-6 flex items-center justify-center rounded-full mb-1 ${
                    isToday ? "bg-[#002FA7] text-white font-bold" : "text-slate-500"
                  }`}>{day}</div>
                  <div className="flex flex-wrap gap-0.5">
                    {dayTasks.slice(0, 4).map(t => (
                      <span
                        key={t.task_id}
                        className={`w-2 h-2 rounded-full ${hasOverdue && PRIORITY_DOT[t.priority] ? "bg-[#FF007A]" : PRIORITY_DOT[t.priority] || "bg-[#374151]"}`}
                        title={t.title}
                      />
                    ))}
                    {dayTasks.length > 4 && (
                      <span className="text-[9px] text-slate-600 font-mono ml-0.5">+{dayTasks.length - 4}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected day task list */}
      {selected && (
        <div className="mt-5 border border-[#1F2937] rounded-lg overflow-hidden" data-testid="cal-day-panel">
          <div className="px-4 py-3 border-b border-[#1F2937] bg-[#0D1117] flex items-center justify-between">
            <span className="text-sm font-semibold text-white">{MONTHS[month]} {selected}</span>
            <span className="text-xs text-slate-500 font-mono">{selectedTasks.length} task{selectedTasks.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="divide-y divide-[#1F2937]">
            {selectedTasks.map(t => (
              <div key={t.task_id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#111827] transition group">
                <StatusCycler taskId={t.task_id} status={t.status} onCycle={updateStatus} />
                <span className={`flex-1 text-sm truncate ${t.status === "done" ? "line-through text-slate-500" : "text-white"}`}>{t.title}</span>
                {t.project?.name && (
                  <Link to={`/workspace/${t.project_id}`} className="text-xs text-slate-600 hover:text-[#00F5D4] truncate max-w-[140px] transition">
                    {t.project.name}
                  </Link>
                )}
                {t.priority && (
                  <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm ${PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.medium}`}>
                    {t.priority[0].toUpperCase()}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(tasksByDay).length === 0 && (
        <p className="text-center text-sm text-slate-600 mt-8">No tasks with due dates this month.</p>
      )}
    </div>
  );
};

const BUCKET_META = {
  overdue:   { label: "Overdue",            icon: AlertTriangle, accent: "text-[#FF007A]",  bar: "bg-[#FF007A]" },
  today:     { label: "Today",              icon: Clock,         accent: "text-[#F59E0B]",  bar: "bg-[#F59E0B]" },
  this_week: { label: "This week",          icon: CalendarDays,  accent: "text-[#00F5D4]",  bar: "bg-[#00F5D4]" },
  later:     { label: "Later",              icon: Calendar,      accent: "text-[#9CA3AF]",  bar: "bg-[#6B7280]" },
  no_due:    { label: "No due date",        icon: ListChecks,    accent: "text-[#6B7280]",  bar: "bg-[#374151]" },
  done:      { label: "Recently completed", icon: CheckCircle2,  accent: "text-[#00F5D4]",  bar: "bg-[#00F5D4]" },
};
const ORDER = ["overdue", "today", "this_week", "later", "no_due", "done"];

const PRIORITY_BADGE = {
  low:      "bg-[#1F2937] text-[#9CA3AF] border border-[#374151]",
  medium:   "bg-[#00F5D4]/10 text-[#00F5D4] border border-[#00F5D4]/20",
  high:     "bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20",
  critical: "bg-[#FF007A]/10 text-[#FF007A] border border-[#FF007A]/20",
};

// Status cycle: todo → in_progress → review → done
const STATUS_CYCLE = ["todo", "in_progress", "review", "done"];
const STATUS_LABEL = { todo: "To do", in_progress: "In progress", review: "Review", done: "Done" };
const STATUS_DOT   = { todo: "bg-[#374151]", in_progress: "bg-[#002FA7]", review: "bg-[#F59E0B]", done: "bg-[#00F5D4]" };

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

const StatusCycler = ({ taskId, status, onCycle }) => {
  const [busy, setBusy] = useState(false);
  const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(status) + 1) % STATUS_CYCLE.length];

  const cycle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await onCycle(taskId, next);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={cycle}
      disabled={busy}
      title={`Status: ${STATUS_LABEL[status] || status} → click to advance`}
      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border-2 transition-all hover:scale-110 ${
        status === "done"
          ? "bg-[#00F5D4] border-[#00F5D4]"
          : "border-[#374151] hover:border-[#00F5D4] bg-transparent"
      }`}
      data-testid={`mytask-status-${taskId}`}
    >
      {busy
        ? <Loader2 className="w-2.5 h-2.5 animate-spin text-white" />
        : status === "done"
          ? <CheckCircle2 className="w-3 h-3 text-[#030712]" strokeWidth={3} />
          : <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status] || "bg-[#374151]"}`} />
      }
    </button>
  );
};

const SavedViewsMenu = ({ views, onApply, onSave, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#111827] border border-[#1F2937] text-xs font-medium text-slate-400 hover:text-white transition"
        data-testid="saved-views-btn"
      >
        <Bookmark className="w-3.5 h-3.5" /> Views <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-64 bg-[#111827] border border-[#1F2937] rounded-lg shadow-xl z-50 py-1" data-testid="saved-views-menu">
            {views.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500">No saved views yet.</div>
            ) : (
              views.map((v) => (
                <div key={v.view_id} className="flex items-center justify-between px-3 py-1.5 hover:bg-[#1F2937] group">
                  <button onClick={() => { onApply(v); setOpen(false); }} className="text-xs text-slate-300 hover:text-white text-left truncate flex-1">
                    {v.name} {!v.is_mine && <span className="text-[9px] text-slate-600">(team)</span>}
                  </button>
                  {v.is_mine && (
                    <button onClick={() => onDelete(v)} className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-rose-400 text-[10px] ml-2">✕</button>
                  )}
                </div>
              ))
            )}
            <div className="border-t border-[#1F2937] mt-1 pt-1">
              <button onClick={() => { onSave(); setOpen(false); }} className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#00F5D4] hover:bg-[#1F2937]">
                <BookmarkPlus className="w-3.5 h-3.5" /> Save current view…
              </button>
              <Link to="/saved-views" onClick={() => setOpen(false)} className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 hover:text-white hover:bg-[#1F2937]">
                Manage saved views
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const MyTasks = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get("priority") || "all");
  const [view, setView] = useState("list"); // "list" | "calendar"
  const [savedViews, setSavedViews] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/tasks/my");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load inbox");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSavedViews = useCallback(async () => {
    try {
      const { data: d } = await api.get("/saved-views?view_type=my_tasks");
      setSavedViews(d || []);
    } catch {
      // non-critical — saved views menu just stays empty
    }
  }, []);

  useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

  const applySavedView = (v) => {
    const f = v.filters || {};
    setSearch(f.search || "");
    setPriorityFilter(f.priority || "all");
  };

  const saveCurrentView = async () => {
    const name = window.prompt("Name this view:");
    if (!name || !name.trim()) return;
    try {
      await api.post("/saved-views", {
        view_type: "my_tasks",
        name: name.trim(),
        filters: { search, priority: priorityFilter },
      });
      toast.success("View saved");
      loadSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save view");
    }
  };

  const deleteSavedView = async (v) => {
    try {
      await api.delete(`/saved-views/${v.view_id}`);
      toast.success("View deleted");
      loadSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete view");
    }
  };

  const updateStatus = async (taskId, status) => {
    try {
      await api.patch(`/tasks/${taskId}/status`, { status });
      toast.success(`→ ${STATUS_LABEL[status] || status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update task");
    }
  };

  const counts = data?.counts || {};
  const allTasks = data?.tasks || [];

  const filtered = useMemo(() => {
    let tasks = allTasks;
    if (search.trim()) {
      const q = search.toLowerCase();
      tasks = tasks.filter((t) =>
        t.title?.toLowerCase().includes(q) || t.project?.name?.toLowerCase().includes(q)
      );
    }
    if (priorityFilter !== "all") {
      tasks = tasks.filter((t) => t.priority === priorityFilter);
    }
    return tasks;
  }, [allTasks, search, priorityFilter]);

  const grouped = useMemo(
    () => ORDER.map((k) => ({ key: k, items: filtered.filter((t) => t.due_bucket === k) })),
    [filtered],
  );

  const totalOpen =
    (counts.overdue || 0) + (counts.today || 0) + (counts.this_week || 0) +
    (counts.later || 0) + (counts.no_due || 0);

  if (loading) return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#00F5D4]" />
    </div>
  );

  return (
    <DarkPage>
      <PageHeader
        eyebrow="Personal"
        title="My Tasks"
        description="Every task across every project assigned to you — grouped by what needs your attention now."
        actions={
          <div className="flex items-center gap-2">
            <SavedViewsMenu views={savedViews} onApply={applySavedView} onSave={saveCurrentView} onDelete={deleteSavedView} />
            <div className="flex items-center bg-[#111827] border border-[#1F2937] rounded-lg p-0.5">
              <button
                onClick={() => setView("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${view === "list" ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-white"}`}
                data-testid="view-list-btn"
              >
                <LayoutList className="w-3.5 h-3.5" /> List
              </button>
              <button
                onClick={() => setView("calendar")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition ${view === "calendar" ? "bg-[#1F2937] text-white" : "text-slate-500 hover:text-white"}`}
                data-testid="view-calendar-btn"
              >
                <Calendar className="w-3.5 h-3.5" /> Calendar
              </button>
            </div>
            <button
              onClick={() => downloadCsv("/tasks/export", "my-tasks.csv")}
              className="p-2 text-slate-400 hover:text-white transition"
              title="Export tasks to CSV"
              data-testid="export-tasks-btn"
            >
              <Download className="w-4 h-4" />
            </button>
            <button onClick={load} className="p-2 text-slate-400 hover:text-white transition" title="Refresh" data-testid="refresh-my-tasks">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-px bg-[#1F2937] border-b border-[#1F2937]">
        {ORDER.map((k) => {
          const meta = BUCKET_META[k];
          const Icon = meta.icon;
          return (
            <div key={k} className="bg-[#111827] px-4 py-4" data-testid={`kpi-${k}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280]">
                <Icon className={`w-3 h-3 ${meta.accent}`} strokeWidth={1.7} /> {meta.label}
              </div>
              <div className={`font-mono font-bold text-3xl tracking-tight mt-2 ${counts[k] > 0 ? meta.accent : "text-[#374151]"}`}>
                {counts[k] || 0}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="px-5 sm:px-8 lg:px-12 py-4 border-b border-[#1F2937] flex flex-wrap gap-3 items-center bg-[#030712]">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6B7280]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks or projects…"
            className="pl-9 h-9 bg-[#111827] border-[#1F2937] text-sm text-white placeholder:text-[#4B5563] rounded-md focus:border-[#00F5D4]/40"
            data-testid="mytask-search"
          />
        </div>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36 h-9 bg-[#111827] border-[#1F2937] text-sm text-white rounded-md" data-testid="priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#1F2937]">
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        {(search || priorityFilter !== "all") && (
          <button
            onClick={() => { setSearch(""); setPriorityFilter("all"); }}
            className="text-xs text-[#6B7280] hover:text-white transition"
            data-testid="clear-filters"
          >
            Clear filters · {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </button>
        )}
      </div>

      {view === "calendar" ? (
        <TaskCalendar tasks={allTasks} updateStatus={updateStatus} />
      ) : totalOpen === 0 && !search && priorityFilter === "all" ? (
        <div className="px-5 sm:px-8 lg:px-12 py-16 text-center">
          <Inbox className="w-12 h-12 mx-auto text-[#374151]" strokeWidth={1.3} />
          <div className="font-display font-black text-3xl tracking-tighter mt-4 text-white">Inbox zero.</div>
          <p className="text-sm text-[#6B7280] mt-2 max-w-md mx-auto">
            You have nothing assigned. {(counts.done || 0) > 0 && `Nice work — ${counts.done} done already.`}
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-5 sm:px-8 lg:px-12 py-16 text-center text-sm text-[#6B7280]" data-testid="no-filter-results">
          No tasks match your filters.
        </div>
      ) : (
        <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-10">
          {grouped.map((g) => {
            if (g.items.length === 0) return null;
            const meta = BUCKET_META[g.key];
            const Icon = meta.icon;
            return (
              <section key={g.key} data-testid={`bucket-section-${g.key}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Icon className={`w-4 h-4 ${meta.accent}`} strokeWidth={1.7} />
                  <h2 className={`font-display font-bold tracking-tight text-base ${meta.accent}`}>{meta.label}</h2>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#4B5563] font-mono">{g.items.length}</span>
                  <div className="h-px bg-[#1F2937] flex-1" />
                </div>
                <div className="border border-[#1F2937] divide-y divide-[#1F2937]" data-testid={`bucket-list-${g.key}`}>
                  {g.items.map((t) => (
                    <div
                      key={t.task_id}
                      className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-[#111827] group transition-colors"
                      data-testid={`mytask-row-${t.task_id}`}
                    >
                      {/* Status cycler */}
                      <div className="col-span-1">
                        <StatusCycler taskId={t.task_id} status={t.status} onCycle={updateStatus} />
                      </div>

                      {/* Title */}
                      <div className={`col-span-5 min-w-0 ${t.status === "done" ? "line-through text-[#4B5563]" : ""}`}>
                        <div className="truncate font-medium text-sm flex items-center gap-2 text-white">
                          <span className="text-[9px] uppercase tracking-[0.2em] px-1 py-0.5 bg-[#1F2937] text-[#6B7280] flex-shrink-0">
                            {t.item_type}
                          </span>
                          {t.repeat_interval && (
                            <span className="flex items-center gap-0.5 text-[9px] uppercase tracking-[0.15em] px-1 py-0.5 bg-[#002FA7]/20 text-[#60A5FA] flex-shrink-0" title={`Repeats ${t.repeat_interval}`}>
                              <Repeat className="w-2.5 h-2.5" />{t.repeat_interval}
                            </span>
                          )}
                          {t.title}
                        </div>
                        {/* Status label */}
                        <div className={`text-[10px] mt-0.5 font-mono ${STATUS_DOT[t.status] ? "" : "text-[#4B5563]"}`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${STATUS_DOT[t.status] || "bg-[#374151]"}`} />
                          <span className="text-[#6B7280]">{STATUS_LABEL[t.status] || t.status}</span>
                        </div>
                      </div>

                      {/* Project */}
                      <div className="col-span-3 min-w-0">
                        {t.project && (
                          <Link
                            to={`/workspace/${t.project_id}`}
                            className="text-xs text-[#6B7280] hover:text-[#00F5D4] truncate flex items-center gap-1 transition-colors"
                          >
                            {t.project.name}
                            <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                          </Link>
                        )}
                      </div>

                      {/* Due date */}
                      <div className="col-span-2 text-right">
                        {t.due_date && (
                          <span className={`text-xs font-mono ${g.key === "overdue" ? "text-[#FF007A] font-semibold" : "text-[#9CA3AF]"}`}>
                            {fmtDate(t.due_date)}
                          </span>
                        )}
                      </div>

                      {/* Priority */}
                      <div className="col-span-1 text-right">
                        {t.priority && (
                          <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm ${PRIORITY_BADGE[t.priority] || PRIORITY_BADGE.medium}`}>
                            {t.priority[0].toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </DarkPage>
  );
};

export default MyTasks;
